/**
 * MessageFilters
 * Barra de filtros para la seccion de mensajes del expediente.
 * Permite alternar entre "Solo credito" / "Todos" y seleccionar subcategorias.
 */
import { Filter } from 'lucide-react';
import type { MessagesFilters } from '../../hooks/useExpedienteMessages';
import type { CreditSubCategory } from '@/types/expedientes.types';

const SUB_CATEGORY_OPTIONS: Array<{ value: CreditSubCategory; label: string }> = [
  { value: 'disbursement',    label: 'Desembolso' },
  { value: 'eligibility',     label: 'Elegibilidad' },
  { value: 'level_up',        label: 'Subida de nivel' },
  { value: 'reminder',        label: 'Recordatorio' },
  { value: 'payment',         label: 'Pago' },
  { value: 'default_warning', label: 'Aviso mora' },
  { value: 'paid_off',        label: 'Liquidación' },
  { value: 'lien',            label: 'Gravamen' },
];

interface Props {
  filters: MessagesFilters;
  onChange: (patch: Partial<MessagesFilters>) => void;
}

export function MessageFilters({ filters, onChange }: Props) {
  const showAll = filters.category === 'all';

  // Alterna entre "solo crédito" y "todos"
  const toggleAll = () => onChange({ category: showAll ? 'credit' : 'all', subCategory: [] });

  // Agrega o quita una subcategoría del arreglo de filtros
  const toggleSubCat = (v: CreditSubCategory) => {
    const set = new Set(filters.subCategory);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange({ subCategory: Array.from(set) });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
      <Filter size={14} className="text-muted-foreground shrink-0" />

      <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!showAll}
          onChange={toggleAll}
          className="rounded border-border"
        />
        Solo crédito
      </label>

      {/* Chips de subcategoría: visibles solo cuando filtro = crédito */}
      {filters.category !== 'all' && (
        <div className="flex flex-wrap gap-1">
          {SUB_CATEGORY_OPTIONS.map(opt => {
            const active = filters.subCategory.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleSubCat(opt.value)}
                className={`text-xs px-2 py-0.5 rounded-full border transition ${
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-card text-muted-foreground hover:border-primary/50'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
